import type { NamedObject, Organization, OrganizationTag} from '@stamhoofd/structures';
import { AccessRight, PermissionsResourceType } from '@stamhoofd/structures';
import { useAuth, usePlatform } from '../../hooks';

export function useEventPermissions() {
    const auth = useAuth();
    const platform = usePlatform();
    const permissions = auth.permissions;

    function canWriteSome() {
        if (!permissions) {
            return false;
        }

        return permissions.hasAccessRightForSomeResourceOfType(PermissionsResourceType.OrganizationTags, AccessRight.EventWrite) || permissions.hasAccessRightForSomeResourceOfType(PermissionsResourceType.Groups, AccessRight.EventWrite);
    }

    function canWriteAllGroupEvents() {
        if (!permissions) {
            return false;
        }
        return permissions.hasAccessRightForAllResourcesOfType(PermissionsResourceType.Groups, AccessRight.EventWrite);
    }

    function canWriteAllTagEvents() {
        if (!permissions) {
            return false;
        }
        return permissions.hasAccessRightForAllResourcesOfType(PermissionsResourceType.OrganizationTags, AccessRight.EventWrite);
    }

    function groupsToFilterEventsOn(): string[] | null {
        return filterEventsOn(PermissionsResourceType.Groups);
    }

    function tagsToFilterEventsOn(): string[] | null {
        return filterEventsOn(PermissionsResourceType.OrganizationTags);
    }

    function isGroupEnabledOperatorFactory() {
        if (!permissions) {
            return () => false;
        }

        return (group: NamedObject) => permissions.hasResourceAccessRight(PermissionsResourceType.Groups, group.id, AccessRight.EventWrite);
    }

    function isTagEnabledPredicateFactory() {
        if (!permissions) {
            return () => false;
        }

        return (tag: OrganizationTag) => permissions.hasResourceAccessRight(PermissionsResourceType.OrganizationTags, tag.id, AccessRight.EventWrite);
    }

    function canAdminEventForExternalOrganization(organization: Organization) {
        const organizationPermissions = auth.user?.permissions?.forOrganization(organization, platform.value);
        return organizationPermissions?.hasAccessRight(AccessRight.EventWrite) ?? false;
    }

    function filterEventsOn(type: PermissionsResourceType): string[] | null {
        if (!permissions) {
            return null;
        }

        if (permissions.hasAccessRightForAllResourcesOfType(type, AccessRight.EventWrite)) {
            return null;
        }

        const result = new Set<string>();

        const ressources = permissions.resources.get(type);
        if (ressources) {
            for (const [tagId, permissions] of ressources.entries()) {
                if (permissions.hasAccessRight(AccessRight.EventWrite)) {
                    result.add(tagId);
                }
            }
        }

        return [...result];
    }

    return {
        canWriteSome,
        canWriteAllGroupEvents,
        canWriteAllTagEvents,
        isGroupEnabledOperatorFactory,
        isTagEnabledPredicateFactory,
        canAdminEventForExternalOrganization,
        tagsToFilterEventsOn,
        groupsToFilterEventsOn,
    };
}
